import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { db } from '@/config/firebase'
import { doc, setDoc, serverTimestamp } from 'firebase/firestore'
import { useAuthStore } from '@/store/authStore'
import toast from 'react-hot-toast'
import { Building2, Rocket, ArrowRight } from 'lucide-react'
import { PLANS } from '@/utils/plans'

export default function CreateOrgPage() {
    const navigate = useNavigate()
    const { user } = useAuthStore()
    const [name, setName] = useState('')
    const [loading, setLoading] = useState(false)

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!user || !name.trim()) return

        setLoading(true)
        const orgId = crypto.randomUUID()
        const orgCode = Math.random().toString(36).substring(2, 8).toUpperCase()

        try {
            const orgRef = doc(db, 'organizations', orgId)

            await setDoc(orgRef, {
                id: orgId,
                name: name.trim(),
                orgCode,
                ownerId: user.uid,
                plan: 'lite',
                branding: {},
                pixConfig: {
                    key: '',
                    instructions: 'Entre em contato com o administrador para pagar via Pix.',
                },
                createdAt: serverTimestamp(),
            })

            // Salvar na subcoleção para histórico/admin local
            const memberSubRef = doc(db, `organizations/${orgId}/members`, user.uid)
            await setDoc(memberSubRef, {
                uid: user.uid,
                role: 'owner',
                joinedAt: serverTimestamp(),
            })

            // Salvar no root members para busca global do App.tsx
            const memberRootRef = doc(db, 'members', `${user.uid}_${orgId}`)
            const memberData = {
                userId: user.uid,
                orgId: orgId,
                role: 'owner',
                joinedAt: serverTimestamp(),
            }
            await setDoc(memberRootRef, memberData)

            // Atualizar o store localmente para feedback imediato
            const newOrg = {
                id: orgId,
                name: name.trim(),
                orgCode,
                ownerId: user.uid,
                plan: 'lite',
                branding: {},
                pixConfig: { key: '', instructions: 'Entre em contato com o administrador para pagar via Pix.' },
                createdAt: new Date(),
            } as any

            useAuthStore.getState().setOrganizations([newOrg])
            useAuthStore.getState().setCurrentOrg(newOrg)
            useAuthStore.getState().setUserRole('owner')
            localStorage.setItem('spacerub_current_org_id', orgId)

            toast.success('Organização criada com sucesso!')
            navigate('/dashboard')
        } catch (error: any) {
            console.error('Erro ao criar organização:', error)
            toast.error('Erro ao criar organização: ' + error.message)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="h-screen bg-white flex flex-col justify-center overflow-hidden font-sans relative">
            {/* Background Decorative Elements */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#d4a94d]/5 rounded-full blur-[100px] -mr-40 -mt-40 pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#001c28]/5 rounded-full blur-[100px] -ml-40 -mb-40 pointer-events-none"></div>

            <div className="max-w-md mx-auto w-full px-4 h-full flex flex-col justify-center animate-in fade-in zoom-in duration-700 relative z-10">
                <div className="text-center mb-6 shrink-0">
                    <div className="w-40 h-40 mx-auto mb-2">
                        <img src="https://apprub.com.br/space/logo.png" className="w-full h-full object-contain" alt="SpaceRub Logo" />
                    </div>
                    <h2 className="text-3xl font-black text-[#001c28] tracking-tighter uppercase italic leading-tight">
                        Criar Organização
                    </h2>
                    <p className="text-[#001c28] font-bold uppercase tracking-widest text-xs mt-1">
                        Comece a gerenciar hoje mesmo
                    </p>
                </div>

                <div className="bg-white p-6 md:p-8 rounded-[2.5rem] shadow-[0_15px_50px_-12px_rgba(0,28,40,0.15)] border-2 border-[#001c28]/5 relative overflow-hidden shrink-0">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-[#001c28]/5 rounded-bl-[3rem] -mr-12 -mt-12 pointer-events-none"></div>

                    <form className="space-y-5" onSubmit={handleCreate}>
                        <div className="space-y-2">
                            <label htmlFor="name" className="text-[10px] font-black text-[#001c28] uppercase tracking-widest ml-1">
                                Nome da Organização
                            </label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                                    <Building2 className="h-5 w-5 text-[#001c28]/40 group-focus-within:text-[#d4a94d] transition-colors" />
                                </div>
                                <input
                                    id="name"
                                    type="text"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Ex: Condomínio Solaris"
                                    className="w-full bg-[#001c28]/[0.02] border-2 border-[#001c28]/10 focus:bg-white focus:border-[#d4a94d] rounded-2xl pl-12 pr-4 py-4 text-sm font-bold text-[#001c28] outline-none transition-all placeholder:text-[#001c28]/20"
                                />
                            </div>
                        </div>

                        <div className="bg-[#001c28]/5 p-5 rounded-2xl border border-[#001c28]/10">
                            <div className="flex space-x-3">
                                <div className="bg-[#001c28] p-2.5 rounded-xl text-[#d4a94d] flex-shrink-0 shadow-lg shadow-[#001c28]/10">
                                    <Rocket className="w-5 h-5" />
                                </div>
                                <div className="space-y-1">
                                    <h3 className="text-xs font-black text-[#001c28] uppercase tracking-tight">SpaceRub Lite Ativo</h3>
                                    <ul className="space-y-1">
                                        {PLANS.lite.features.map((item, i) => (
                                            <li key={i} className="text-[10px] text-[#001c28] font-medium flex items-center">
                                                <span className="w-1 h-1 bg-[#d4a94d] rounded-full mr-2"></span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="pt-2 space-y-3">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center items-center space-x-2 py-4 rounded-xl text-xs font-black uppercase tracking-widest text-white bg-[#d4a94d] hover:bg-[#001c28] shadow-lg shadow-[#d4a94d]/30 hover:shadow-[#001c28]/30 transition-all active:scale-95 disabled:opacity-50"
                            >
                                {loading ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                ) : (
                                    <>
                                        <span>Confirmar e Criar</span>
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate('/join-org')}
                                className="w-full text-center text-[10px] font-black text-[#001c28]/40 hover:text-[#001c28] uppercase tracking-widest transition-colors"
                            >
                                Ou entrar em uma organização existente
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}
